/**
 * "Kommer tillbaka" (pedagogy spec §3.3.5 item 6): the re-tests Today will serve in the next
 * week and the flashcards that fall due, one row each. A problem row names the topic and which
 * kind of problem comes ("ny variant"), never the item; the flashcards are one collapsed row
 * per day. Today and anything overdue read "idag".
 */
import { CalendarDays } from "lucide-react";
import { Badge, Card } from "@client/components/ui";
import { useT, useTp } from "@client/i18n";
import { COMING_BACK_HORIZON_DAYS } from "@shared/evidence";
import { useDayLabel } from "./dayLabel";
import type { ComingBackRow } from "./types";

export interface ComingBackCardProps {
  rows: ComingBackRow[];
  /** A topic's name in the active language. */
  topicLabel: (topicId: string) => string;
  /** The day key "idag" is read from; defaults to the clock. */
  today?: string;
}

export function ComingBackCard({ rows, topicLabel, today }: ComingBackCardProps) {
  const t = useT();
  const tp = useTp();
  const dayLabel = useDayLabel(today);
  const title = t("progress.comingBack.title");

  return (
    <Card title={title} compact>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">
          {tp("progress.comingBack.empty", COMING_BACK_HORIZON_DAYS)}
        </p>
      ) : (
        <ul aria-label={title} className="divide-y divide-ink-100 dark:divide-ink-900">
          {rows.map((row, index) => (
            <li
              key={`${index}-${row.source}-${row.dueDay}`}
              className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
            >
              <CalendarDays
                className="h-4 w-4 shrink-0 text-ink-400 dark:text-ink-500"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-medium text-ink-800 dark:text-ink-100">
                  {row.source === "problem"
                    ? topicLabel(row.topicId)
                    : tp("progress.comingBack.cards", row.cards)}
                </p>
                {row.source === "problem" && (
                  <p className="text-xs text-ink-600 dark:text-ink-400">
                    {t(`progress.comingBack.variant.${row.variant}`)}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3">
                <time
                  dateTime={row.dueDay}
                  className="text-sm tabular-nums text-ink-700 dark:text-ink-200"
                >
                  {dayLabel(row.dueDay)}
                </time>
                <Badge tone="neutral">{t("progress.comingBack.badge")}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default ComingBackCard;
